type TAllKeys<T> = T extends any ? keyof T : never;

type TIndexValue<T, K extends PropertyKey, D = never> = T extends any ? (K extends keyof T ? T[K] : D) : never;

type TPartialKeys<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>> extends infer O
    ? { [P in keyof O]: O[P] }
    : never;

type TFunction = (...a: any[]) => any;

type TPrimitives = string | number | boolean | bigint | symbol | Date | TFunction;

type TMerged<T> = [T] extends [Array<any>]
    ? { [K in keyof T]: TMerged<T[K]> }
    : [T] extends [TPrimitives]
      ? T
      : [T] extends [object]
        ? TPartialKeys<{ [K in TAllKeys<T>]: TMerged<TIndexValue<T, K>> }, never>
        : T;

/**
 * Determines if a value is a plain object that can be deeply merged.
 *
 * A plain object is defined as:
 * - An object created by the Object constructor (prototype is Object.prototype)
 * - An object with no prototype (created with Object.create(null))
 * - Not an array, Date, RegExp, or other built-in object types
 * - Not a custom class instance
 *
 * @param obj - The value to test
 * @returns True if the value is a plain object suitable for deep merging
 *
 * @internal This is an internal utility function
 */
// istanbul ignore next
const isObject = (obj: any) => {
    if (typeof obj === 'object' && obj !== null) {
        if (typeof Object.getPrototypeOf === 'function') {
            const prototype = Object.getPrototypeOf(obj);
            return prototype === Object.prototype || prototype === null;
        }

        return Object.prototype.toString.call(obj) === '[object Object]';
    }

    return false;
};

interface IObject {
    [key: string]: any;
}

/**
 * Recursively merges multiple objects into a single object with deep property merging.
 *
 * This function performs a deep merge operation where:
 * - Primitive values are overridden by later objects (left-to-right precedence)
 * - Nested objects are recursively merged rather than replaced
 * - Arrays are merged by default (concatenated with duplicates removed)
 * - Special properties (`__proto__`, `constructor`, `prototype`) are ignored for security
 * - Only plain objects are deeply merged; other objects (Date, custom classes) are replaced
 *
 * @template T - Array of object types being merged
 * @param objects - The objects to merge. Must be plain objects, not arrays or primitives
 * @returns A new object containing the merged properties from all input objects
 *
 * @throws {TypeError} When any argument is an array instead of an object
 * @throws {Error} When circular references are encountered (causes stack overflow)
 *
 * @example Basic object merging
 * ```ts
 * const obj1 = { a: 1, b: { c: 2 } };
 * const obj2 = { b: { d: 3 }, e: 4 };
 * const merged = deepMerge(obj1, obj2);
 * // Result: { a: 1, b: { c: 2, d: 3 }, e: 4 }
 * ```
 *
 * @example Array merging behavior
 * ```ts
 * const obj1 = { items: [1, 2, 3] };
 * const obj2 = { items: [3, 4, 5] };
 * const merged = deepMerge(obj1, obj2);
 * // Result: { items: [1, 2, 3, 4, 5] } // duplicates removed
 * ```
 *
 * @example Multiple object merging
 * ```ts
 * const base = { a: 1 };
 * const config = { b: 2, nested: { x: 10 } };
 * const overrides = { c: 3, nested: { y: 20 } };
 * const result = deepMerge(base, config, overrides);
 * // Result: { a: 1, b: 2, c: 3, nested: { x: 10, y: 20 } }
 * ```
 *
 * @example Undefined value handling
 * ```ts
 * const obj1 = { a: 1, b: 2 };
 * const obj2 = { a: undefined, c: 3 };
 * const merged = deepMerge(obj1, obj2);
 * // Result: { a: undefined, b: 2, c: 3 } // undefined overrides existing value
 * ```
 *
 * @see {@link deepMerge.withOptions} For customizing merge behavior
 * @see {@link deepMerge.options} For current global options
 */
export const deepMerge = <T extends IObject[]>(...objects: T): TMerged<T[number]> =>
    objects.reduce((result, current) => {
        if (Array.isArray(current)) {
            throw new TypeError('Arguments provided to ts-deepmerge must be objects, not arrays.');
        }

        Object.keys(current).forEach((key) => {
            if (['__proto__', 'constructor', 'prototype'].includes(key)) {
                return;
            }

            if (Array.isArray(result[key]) && Array.isArray(current[key])) {
                result[key] = deepMerge.options.mergeArrays
                    ? deepMerge.options.uniqueArrayItems
                        ? Array.from(new Set((result[key] as unknown[]).concat(current[key])))
                        : [...result[key], ...current[key]]
                    : current[key];
            } else if (isObject(result[key]) && isObject(current[key])) {
                result[key] = deepMerge(result[key] as IObject, current[key] as IObject);
            } else {
                result[key] =
                    current[key] === undefined
                        ? deepMerge.options.allowUndefinedOverrides
                            ? current[key]
                            : result[key]
                        : current[key];
            }
        });

        return result;
    }, {}) as any;

interface IOptions {
    /**
     * When `true`, values explicitly provided as `undefined` will override existing values, though properties that are simply omitted won't affect anything.
     * When `false`, values explicitly provided as `undefined` won't override existing values.
     *
     * Default: `true`
     */
    allowUndefinedOverrides: boolean;

    /**
     * When `true` it will merge array properties.
     * When `false` it will replace array properties with the last instance entirely instead of merging their contents.
     *
     * Default: `true`
     */
    mergeArrays: boolean;

    /**
     * When `true` it will ensure there are no duplicate array items.
     * When `false` it will allow duplicates when merging arrays.
     *
     * Default: `true`
     */
    uniqueArrayItems: boolean;
}

const defaultOptions: IOptions = {
    allowUndefinedOverrides: true,
    mergeArrays: true,
    uniqueArrayItems: true,
};

deepMerge.options = defaultOptions;

/**
 * Performs a deep merge with custom options, temporarily overriding global options.
 *
 * This method allows you to customize the merge behavior for a single operation
 * without permanently changing the global options. After the merge completes,
 * the global options are automatically restored to their defaults.
 *
 * @template T - Array of object types being merged
 * @param options - Partial options object to override default behavior
 * @param objects - The objects to merge with the specified options
 * @returns A new object containing the merged properties with custom merge behavior
 *
 * @example Custom array merging behavior
 * ```ts
 * const obj1 = { items: [1, 2, 3] };
 * const obj2 = { items: [3, 4, 5] };
 *
 * // Replace arrays instead of merging them
 * const replaced = deepMerge.withOptions(
 *   { mergeArrays: false },
 *   obj1,
 *   obj2
 * );
 * // Result: { items: [3, 4, 5] }
 *
 * // Allow duplicate items when merging
 * const withDuplicates = deepMerge.withOptions(
 *   { uniqueArrayItems: false },
 *   obj1,
 *   obj2
 * );
 * // Result: { items: [1, 2, 3, 3, 4, 5] }
 * ```
 *
 * @example Preventing undefined overrides
 * ```ts
 * const obj1 = { a: 1, b: 2 };
 * const obj2 = { a: undefined, c: 3 };
 *
 * const result = deepMerge.withOptions(
 *   { allowUndefinedOverrides: false },
 *   obj1,
 *   obj2
 * );
 * // Result: { a: 1, b: 2, c: 3 } // undefined doesn't override existing value
 * ```
 *
 * @example Multiple option combinations
 * ```ts
 * const result = deepMerge.withOptions(
 *   {
 *     allowUndefinedOverrides: false,
 *     mergeArrays: true,
 *     uniqueArrayItems: false
 *   },
 *   obj1,
 *   obj2,
 *   obj3
 * );
 * ```
 */
deepMerge.withOptions = <T extends IObject[]>(options: Partial<IOptions>, ...objects: T) => {
    deepMerge.options = {
        ...defaultOptions,
        ...options,
    };

    const result = deepMerge(...objects);

    deepMerge.options = defaultOptions;

    return result;
};
